// Remembers where the user started an OAuth connect (QuickBooks / Shopify) so the
// callback page can send them back there (e.g. the onboarding wizard) instead of
// dropping them somewhere unrelated. Stored in localStorage so it survives the
// full-page redirect to the provider and back.
const KEY = 'gt_oauth_return';
const MAX_AGE_MS = 30 * 60 * 1000;

export type OAuthPlatform = 'quickbooks' | 'shopify';

export interface OAuthReturnInfo {
  platform: OAuthPlatform;
  source?: string | null;
  path: string;
  companyId?: number | string | null;
  ts: number;
}

export function saveOAuthReturn(platform: OAuthPlatform, source?: string | null, companyId?: number | string | null): void {
  try {
    const info: OAuthReturnInfo = {
      platform,
      source: source || null,
      path: `${window.location.pathname}${window.location.search}`,
      companyId: companyId ?? null,
      ts: Date.now(),
    };
    localStorage.setItem(KEY, JSON.stringify(info));
  } catch {
    // storage unavailable: callback falls back to its default destination
  }
}

export function consumeOAuthReturn(platform: OAuthPlatform): OAuthReturnInfo | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const info = JSON.parse(raw) as OAuthReturnInfo;
    if (!info || info.platform !== platform || Date.now() - (info.ts || 0) > MAX_AGE_MS) {
      return null;
    }
    localStorage.removeItem(KEY);
    return info;
  } catch {
    return null;
  }
}

/** Where to send the user after a successful connect. */
export function oauthSuccessPath(platform: OAuthPlatform, info: OAuthReturnInfo | null, fallback: string): string {
  if (info?.source === 'onboarding') {
    return `/get-started?${platform}=connected`;
  }
  return fallback;
}
